/**
 * Unsubscribe Page — ProfitSpot
 * CAN-SPAM / GDPR compliant one-click unsubscribe
 * Standalone — no sidebar, works without login (token-based)
 */
import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Loader, Mail, ArrowRight, MailX } from 'lucide-react';
import { getBackendBaseUrl } from '../lib/apiBase';

const API_URL = getBackendBaseUrl();

export default function Unsubscribe() {
  const [status, setStatus] = useState('loading'); // loading | success | already | error
  const [name, setName]     = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token  = params.get('token');

    if (!token) {
      setStatus('error');
      setMessage('No unsubscribe token found. Please use the link from your email.');
      return;
    }

    fetch(`${API_URL}/api/auth/unsubscribe?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        try {
          const data = await res.json();
          if (res.ok && data.success) {
            setName(data.name || '');
            setStatus('success');
          } else {
            setStatus('error');
            setMessage(data.detail || data.message || 'Something went wrong. Please try again.');
          }
        } catch (parseError) {
          setStatus('error');
          setMessage('Something went wrong. Please try again or contact support.');
        }
      })
      .catch(() => {
        setStatus('error');
        setMessage('Network error. Please try again or contact support@profitspot.live');
      });
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a12] flex flex-col items-center justify-center p-6">

      {/* Logo */}
      <div className="mb-10 text-center">
        <a href="/" className="text-2xl font-bold text-[#00ff88] tracking-tight hover:opacity-80 transition-opacity">
          ProfitSpot
        </a>
        <p className="text-xs text-gray-600 mt-1">DeFi Yield Intelligence</p>
      </div>

      {/* Card */}
      <div className="w-full max-w-md bg-[#0f0f1a] border border-gray-800 rounded-2xl p-8 text-center shadow-2xl">

        {/* Loading */}
        {status === 'loading' && (
          <>
            <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mx-auto mb-5">
              <Loader className="w-7 h-7 text-gray-400 animate-spin" />
            </div>
            <h1 className="text-lg font-bold text-white mb-2">Processing your request...</h1>
            <p className="text-sm text-gray-500">Just a moment.</p>
          </>
        )}

        {/* Success */}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#00ff88]/10 border border-[#00ff88]/20 flex items-center justify-center mx-auto mb-5">
              <MailX className="w-7 h-7 text-[#00ff88]" />
            </div>
            <h1 className="text-xl font-bold text-white mb-3" data-testid="unsubscribe-success">
              {name ? `Hi ${name}, you're unsubscribed.` : "You're unsubscribed."}
            </h1>
            <p className="text-sm text-gray-400 mb-6 leading-relaxed">
              You've been removed from all ProfitSpot email alerts. We won't send you
              Smart Yield Alerts or marketing emails going forward.
            </p>

            <div className="bg-[#0a0a12] border border-gray-800 rounded-lg p-4 mb-6 text-left">
              <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wider">Changed your mind?</p>
              <p className="text-xs text-gray-400 leading-relaxed">
                Log in to your account and re-enable email alerts under{' '}
                <strong className="text-gray-300">Account Settings → Email Preferences</strong>.
              </p>
            </div>

            <a
              href="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0a0a12] border border-gray-700 hover:border-gray-500 text-white text-sm font-medium rounded-lg transition-colors"
              data-testid="back-to-app-btn"
            >
              Back to ProfitSpot <ArrowRight className="w-4 h-4" />
            </a>
          </>
        )}

        {/* Error */}
        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-5">
              <XCircle className="w-7 h-7 text-red-400" />
            </div>
            <h1 className="text-xl font-bold text-white mb-3">Something went wrong</h1>
            <p className="text-sm text-gray-400 mb-6 leading-relaxed">{message}</p>

            <div className="bg-[#0a0a12] border border-gray-800 rounded-lg p-4 mb-6 text-left">
              <p className="text-xs text-gray-400 leading-relaxed">
                Need help? Email us at{' '}
                <a href="mailto:support@profitspot.live" className="text-[#00d4ff]">
                  support@profitspot.live
                </a>{' '}
                and we'll manually remove you right away.
              </p>
            </div>

            <a
              href="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0a0a12] border border-gray-700 hover:border-gray-500 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Back to ProfitSpot <ArrowRight className="w-4 h-4" />
            </a>
          </>
        )}
      </div>

      {/* Footer note */}
      <div className="mt-8 text-center">
        <p className="text-xs text-gray-700">
          ProfitSpot · Non-Custodial DeFi Intelligence ·{' '}
          <a href="/privacy" className="text-gray-600 hover:text-gray-400 transition-colors">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}
